import Link from "next/link"
import { useRouter } from "next/router"
import { usePaginatedQuery } from "@blitzjs/rpc"
import { ReportData } from "../types"
import { format, parse } from "date-fns"
import { IoMdSchool } from "react-icons/io"
import { FaSchool, FaBookOpen } from "react-icons/fa"
import { Prisma } from "@prisma/client"
import { HiCalendar } from "react-icons/hi"
import clsx from "clsx"
import { IllustrationClassroom } from "app/core/components/IllustrationClassroom"
import getFluencyTestResults from "../queries/getFluencyTestResults"

const ITEMS_PER_PAGE = 100

type TestResultProps = {
  testResult: Prisma.PromiseReturnType<typeof getFluencyTestResults>["testResults"][0]
  className: any
}

function assertReportData(value: any): asserts value is ReportData {
  if (!value || typeof value !== "object") {
    throw new Error("reportData is missing")
  }
}

const TestResult = ({ testResult, className }: TestResultProps) => {
  const displayGrade = (grade) => {
    if (grade === -1) {
      return "PK"
    } else if (grade === 0) {
      return "K"
    } else {
      return grade
    }
  }
  return (
    <li className={clsx(className)}>
      <Link
        href={`/fluency-test-results/${testResult.id}`}
        className="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out"
      >
        <div className="flex items-center px-4 py-4 sm:px-6">
          <div className="flex items-center flex-1 min-w-0">
            <div className="flex-1 min-w-0 md:grid md:grid-cols-2 md:gap-4 space-y-2 md:space-y-0">
              <div className="space-y-1">
                <div className="text-lg font-semibold text-gray-900 truncate leading-5">
                  {testResult.studentName}
                </div>
                <div className="flex items-center text-sm text-gray-600 text-teal-600 leading-5 space-x-2">
                  <HiCalendar />
                  <span className="truncate">
                    {format(testResult.testCompletionDate, "LLLL d, yyyy")}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600 leading-5 space-x-2">
                  <FaBookOpen className="text-gray-500" />
                  <span className="truncate">{testResult.assignedTestType}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600 leading-5 space-x-2">
                  <FaSchool />
                  <span className="truncate">{testResult.termName}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 leading-5 space-x-2 mr-4">
                  <IoMdSchool />
                  <span className="truncate">Grade {displayGrade(testResult.grade)}</span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </Link>
    </li>
  )
}

export type TestFluencyResultsListProps = {
  admin?: boolean
}

export const TestFluencyResultsList = ({ admin = false }: TestFluencyResultsListProps) => {
  const router = useRouter()
  const page = Number(router.query.page) || 0
  const search = (router.query.search as string) || ""
  const [result] = usePaginatedQuery(getFluencyTestResults, {
    orderBy: admin ? { updatedAt: "desc" } : { id: "desc" },
    skip: ITEMS_PER_PAGE * page,
    take: ITEMS_PER_PAGE,
    forAdmin: admin,
    search,
  })
  if (!result) return null

  const { testResults, hasMore } = result

  const goToPreviousPage = () => router.push({ query: { page: page - 1 } })
  const goToNextPage = () => router.push({ query: { page: page + 1 } })

  if (!testResults.length) {
    return (
      <div className="flex flex-col items-center justify-center space-y-6 pt-16">
        <IllustrationClassroom />
        <h2 className="text-lg font-semibold text-gray-700">No Test Results Yet</h2>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden bg-white shadow sm:rounded-md">
        <ul>
          {testResults.map((testResult, index) => (
            <TestResult
              testResult={testResult}
              key={testResult.id}
              className={index > 0 && "border-t border-gray-200"}
            />
          ))}
        </ul>
      </div>
      <span className="relative z-0 inline-flex shadow-sm">
        <button
          disabled={page === 0}
          onClick={goToPreviousPage}
          type="button"
          className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md leading-5 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150 can-disable"
          aria-label="Previous"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          Previous
        </button>
        <button
          disabled={!hasMore}
          onClick={goToNextPage}
          type="button"
          className="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md leading-5 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150 can-disable"
          aria-label="Next"
        >
          Next
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </span>
    </div>
  )
}
